/**
 * Kérés-hibák felhasználói szövegre fordítása.
 *
 * MIÉRT KELL: a komponensek eddig `err.response?.data?.message || 'Hiba történt…'`
 * mintát használtak. Ez összemossa a három gyökeresen különböző esetet:
 *
 *  1. a szerver válaszolt és megmondta, mi a baj (a magyar indoklás kiolvasása:
 *     `serverErrorMessage`, lásd alább — HÁROMFÉLE törzs-alakot kell tolerálni),
 *  2. a szerver válaszolt, de üzenet nélkül (pl. 403 — a jogosultság-szűrés nem ír testet),
 *  3. a kérés EL SEM ÉRTE a szervert (offline, DNS, megszakadt kapcsolat).
 *
 * A harmadik esetet a régi minta ugyanazzal a mondattal jelentette, mint egy igazi
 * szerverhibát — 2026-09-10-én pontosan ez vitt félre: az adatlap „Hiba történt a profil
 * betöltésekor” üzenetet mutatott, miközben az éles naplóban semmilyen hiba nem volt,
 * mert a kérés soha nem hagyta el a böngészőt. A státuszkód kiírásával a bejelentésből
 * ránézésre eldönthető, melyik esettel van dolgunk.
 *
 * FIGYELEM: axios-hibákra készült. A `fetch` hálózati bukása sima
 * `TypeError('Failed to fetch')`, azon nincs se `response`, se `code`, se `request` —
 * abból ez a helper nem tudja megállapítani, hogy hálózati hiba volt, ezért a tartalék
 * szöveget adja. `fetch`-es híváshoz külön felismerés kellene.
 */

/** Ezt látja a felhasználó, ha a kérés el sem érte a szervert. */
export const NETWORK_ERROR_MESSAGE =
  'Nem sikerült elérni a szervert. Ellenőrizd az internetkapcsolatot, és próbáld újra.';

/**
 * [Web UX 9 / T2, 2026-10-13] EMBERI MONDAT a szerver által NEM indokolt 403-ra és 429-re.
 *
 * MI VOLT A BAJ (lelet: e fájl korábbi 200. sora, `return … \`${fallback} (HTTP ${response.status})\``):
 * a keretrendszer jogosultság-szűrése (`[Authorize(Roles=…)]`) TESTETLEN 403-at ad, ezért a
 * felhasználó pl. „Hiba történt a leiratkozás során (HTTP 403)" mondatot látott — ebből egy klubtag
 * nem érti, hogy egyszerűen nincs joga a művelethez. Ugyanígy a 429 (sebességkorlát), ha a választ nem
 * a mi `OnRejected`-ünk (`backend/PinyoApi/Program.cs`, magyar `{ message }`), hanem a platform vagy egy
 * közbülső réteg adja, törzs vagy saját mondat nélkül.
 *
 * ⚠️ A SZERVER SAJÁT INDOKLÁSA ELŐBBRE VALÓ: ez a mondat CSAK akkor megy ki, ha a `serverErrorMessage`
 * semmit nem talált (pl. a jelenléti ív 403-ának „Csak az eseményhez rendelt edző…" mondata marad).
 *
 * ⚠️ SZÁNDÉKOS ELTÉRÉS A MOBIL TÜKÖRTŐL (`mobile/utils/apiErrors.ts` ma „(HTTP 403)"-at ad): a
 * paritás-őr (`__tests__/apiErrors.parity.test.ts`) ezt a két státuszt kifejezetten, név szerint
 * engedi el a webes oldalon; a mobil átvezetése külön, nyitott döntés.
 *
 * A 404-et SZÁNDÉKOSAN NEM vettük fel: a saját 404-eink mind magyar `{ message }`-et adnak, a testetlen
 * 404 pedig jellemzően útvonal-eltérés (régi kliens / új szerver) — ott a „(HTTP 404)" a hibakereső
 * egyetlen nyoma, egy „már nem létezik" mondat hamis nyomra vinne.
 */
export const FORBIDDEN_MESSAGE = 'Ehhez nincs jogosultságod.';
export const TOO_MANY_REQUESTS_MESSAGE = 'Túl sok próbálkozás, kérjük, várj egy percet.';

/** A státuszhoz tartozó általános magyar mondat, ha van ilyen (csak 403 és 429). */
export function statusSentence(status: number | undefined): string | undefined {
  if (status === 403) return FORBIDDEN_MESSAGE;
  if (status === 429) return TOO_MANY_REQUESTS_MESSAGE;
  return undefined;
}

/**
 * A szerver HÁROM különböző alakban adja meg, mi a baj. Mindhármat olvasni kell.
 *
 * MI VOLT A BAJ (mérve 2026-09-14, lokális stack): ez a helper CSAK a legfelső szintű
 * `data.message`-et olvasta, a szerver viszont háromféle törzset ad — és a másik kettőnél
 * a MAGYAR indoklás SOHA nem jutott ki a felhasználóhoz, csak annyi, hogy „(HTTP 400)"
 * vagy „(HTTP 500)".
 *
 *  (a) CSUPASZ — `{ "message": "A fiók inaktív" }`. Ez működött eddig is.
 *
 *  (b) BURKOLT — `{ "success": false, "error": { "code", "message", "timestamp", "traceId" } }`.
 *      Nyolc kontroller ad ilyet (`CoachAvailability`, `Venues`, `Event`, `EventTypes`,
 *      `PlannedEvents`, `Reports`, `UserRoles`, `Dashboard`), összesen 73 helyen.
 *      Mérve: `POST /api/venues` létező névvel → 409
 *      `{"success":false,"error":{"code":"DUPLICATE_RESOURCE","message":"Ilyen nevű helyszín már létezik",…}}`
 *      — a felhasználó eddig „(HTTP 409)"-et látott.
 *      ⚠️ A SIKERES válasz burkolatát (`{ success: true, data: … }`) NEM itt bontjuk ki:
 *      azt a SERVICE-ek csomagolják ki (`response.data.data || response.data`), és ez a
 *      helper csak hibautakon fut — a sikeres út érintetlen.
 *
 *  (c) MODELL-VALIDÁCIÓ — `{ "errors": { "<mező>": ["magyar mondat"] }, "title": "…" }`.
 *      Mérve: `POST /api/attendance/bulk` `paymentAmount:-100` → 400
 *      `{"errors":{"Attendances[0].PaymentAmount":["A fizetett összeg 0 és 1 000 000 között lehet."]}}`.
 *      A mondat a MI DTO-annotációnkból jön, tehát felhasználónak szóló.
 *
 * 🔴 A `title`-t SOHA nem olvassuk — lásd a `responseErrorMessage` doksiját: az ASP.NET
 * állítja be, és MINDIG ANGOL („One or more validation errors occurred.").
 *
 * 🔴 AZ `errors` SZÓTÁRBÓL CSAK A MAGYAR MONDATOKAT VESSZÜK ÁT. Ugyanabban a szótárban a
 * keretrendszer SAJÁT, angol szövegei is megjelennek, ha egy annotációnak nincs explicit
 * `ErrorMessage`-e vagy a JSON-kötés bukik el. Mérve: `POST /api/auth/login` 5 karakteres
 * jelszóval → `{"errors":{"Password":["The field Password must be a string or array type
 * with a minimum length of '6'."]}}`. Ha ezt átengednénk, ANGOL ASP.NET-szöveg kerülne a
 * végig magyar felületre — pontosan az a hiba, amiért a `title`-t is kizártuk.
 *
 * A szűrő: a mondatban van-e NEM-ASCII (ékezetes) karakter.
 *
 * 🔴🔴 EZ A SZŰRŐ MÁR NEM A FŐ ÚT, ÉS AZ INVARIÁNSA BIZONYÍTOTTAN HAMIS [2026-09-14].
 * NE ÉPÍTS RÁ ÚJ VISELKEDÉST.
 *
 * MIÉRT HAMIS: a szűrő abból indult ki, hogy „ékezet van" ⇔ „a mi magyar mondatunk" — mert a
 * `HungarianUserFacingMessagesTests` forrás-őre megbuktatja az ékezet nélküli
 * `message`/`ErrorMessage` literálokat. A validátor viszont MEGMÉRTE, hogy a keretrendszer
 * KÖTÉSI hibái VISSZAIDÉZIK a felhasználó saját értékét, és attól lesz a mondat nem-ASCII:
 *     GET /api/news/public?limit=%C3%A1rv%C3%ADzt%C5%B1r%C5%91
 *     → {"errors":{"limit":["The value 'árvíztűrő' is not valid."]}}
 * Ez ANGOL mondat, mégis átment volna a szűrőn. A felületről ma nem kiváltható (minden
 * query/route paraméter app-generált), de az első user-gépelt, nem-string paraméternél élesbe
 * fordulna.
 *
 * MI A FŐ ÚT HELYETTE: a SZERVER ad magyar `message`-t. 2026-09-14 óta
 *  • a 400-as modell-validációs válasz `{ message: "<magyar mondatok>", errors: { … } }`
 *    alakú (`backend/PinyoApi/Program.cs`, `InvalidModelStateResponseFactory`), és
 *  • a `ModelBindingMessageProvider` MIND A 11 alapszövege magyar (ugyanott),
 *  • a kötött (request) típusok MINDEN validációs annotációja magyar `ErrorMessage`-t hordoz,
 *    amit forrás-őr kényszerít ki.
 * Ezért a `serverErrorMessage` az (a) `message` ágon áll meg, és ide már EL SEM JUT.
 *
 * MIÉRT MARAD MÉGIS: védelmi tartalék. Ha egy jövőbeli végpont mégis `message` nélküli 400-at
 * adna, a magyar mondat így sem veszik el. De a helyes javítás ilyenkor a SZERVEREN van, nem
 * ennek a szűrőnek a finomításában.
 *
 * A mezőneveket SZÁNDÉKOSAN nem írjuk ki: azok angol, PascalCase útvonalak
 * (`Attendances[0].PaymentAmount`), a felhasználónak semmit nem mondanak — a mondat maga
 * viszont már megnevezi a mezőt („A fizetett összeg…").
 */
const HUNGARIAN_LETTER = /[^\u0000-\u007F]/;

/**
 * A modell-validációs `errors` szótár MAGYAR mondatai, ismétlés nélkül, egy szövegbe fűzve.
 *
 * Miért MINDET, nem csak az elsőt: egy 400-as válasz több FÜGGETLEN hibát is megnevezhet
 * (pl. túl hosszú név ÉS rossz résztvevőszám). Ha csak az elsőt írnánk ki, a felhasználó
 * kijavítja, újra elküldi, és ugyanoda fut — ezért nem vágunk le belőle semmit.
 *
 * Miért nincs felső korlát a mondatok számán: az ASP.NET a tömbös mezőknél MINDEN elemre
 * külön beírja UGYANAZT a mondatot (12 jelenléti sor → 12 azonos szöveg), ezért a
 * duplikátum-szűrés után a gyakorlatban 1–3 mondat marad. Korlátot pont azért nem tettünk,
 * hogy ne tűnhessen el egy valódi, másik hiba.
 */
function messageFromValidationErrors(errors: unknown): string | undefined {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return undefined;

  const sentences: string[] = [];
  Object.values(errors as Record<string, unknown>).forEach((value) => {
    const items = Array.isArray(value) ? value : [value];
    items.forEach((item) => {
      if (typeof item !== 'string') return;
      const text = item.trim();
      if (!text) return;
      if (!HUNGARIAN_LETTER.test(text)) return; // keretrendszertől jött, angol
      if (sentences.indexOf(text) === -1) sentences.push(text);
    });
  });

  return sentences.length ? sentences.join(' ') : undefined;
}

/**
 * A szerver saját MAGYAR indoklása a válasz TÖRZSÉBŐL, mindhárom alakot végigpróbálva.
 * A sorrend a konkrétabbtól az általánosabb felé megy: `message` → `error.message` → `errors`.
 */
export function serverErrorMessage(data: unknown): string | undefined {
  const body = data as any;

  // (a) csupasz `{ message }`
  if (typeof body?.message === 'string' && body.message.trim()) return body.message;

  // (b) burkolt `{ success: false, error: { message } }`
  if (typeof body?.error?.message === 'string' && body.error.message.trim()) return body.error.message;

  // (c) modell-validációs `{ errors: { mező: ["…"] } }`
  return messageFromValidationErrors(body?.errors);
}

/**
 * LEMONDOTT (megszakított) kérés-e? [B-M2, 2026-09-16]
 *
 * ⚠️ A MOBIL TÜKÖRBŐL JÖTT ÁT, MERT A KÖZÖS MAGOT ÉRINTI (`mobile/utils/apiErrors.ts`). Ott a
 * lelet MÉRT: a lejárt munkamenet miatt SZÁNDÉKOSAN lemondott kérés (`services/apiClient.ts:102`
 * `axios.CanceledError`) „hálózati hibaként" minősült, és a felhasználó azt kapta, hogy
 * „Ellenőrizd az internetkapcsolatot" — miközben csak újra be kellett volna lépnie.
 *
 * MIÉRT KELL A WEBEN IS, HA ITT NINCS OLYAN INTERCEPTOR: (1) a két modul közös magja TÜKÖR, és a
 * paritás-őr ugyanazt a viselkedés-táblát kényszeríti ki mindkét oldalon — egy fél úton megtett
 * javítás pont azt a driftet hozná vissza, amiért az őr létezik; (2) a webes hívási utakon a
 * `fetch` + `AbortController` ugyanezt a helyzetet állítja elő (`AbortError`), és a lemondás ott
 * sem HIBA, hanem a mi saját döntésünk.
 *
 * ⚠️ AXIOS-IMPORT NÉLKÜL, SZÁNDÉKOSAN: az `axios.isCancel` ugyanezt a három jelet vizsgálja, de
 * ez a modul a `fetch`-es úton is fut (`responseErrorMessage`), ezért nem kötjük axioshoz.
 */
export function isCanceledRequest(err: any): boolean {
  if (!err) return false;
  return err.code === 'ERR_CANCELED'
    || err.name === 'CanceledError'
    || err.name === 'AbortError'
    || err.__CANCEL__ === true;
}

/**
 * Egy elkapott kérés-hibából felhasználói üzenetet állít elő.
 *
 * @param err a catch-ben kapott hiba (axios-hiba vagy bármi más)
 * @param fallback mit írjunk, ha a szerver válaszolt, de nem mondta meg, mi a baj
 */
export function apiErrorMessage(err: any, fallback: string): string {
  const response = err?.response;

  // A LEMONDOTT kérés nem hálózati hiba [B-M2]: a „nézd meg az internetkapcsolatot" tanács itt
  // hamis nyom lenne. A hívó saját magyar tartalékát adjuk — a lemondásra épp az a helyes
  // viselkedés, hogy a hívó EL SEM MUTATJA (lásd a mobil `apiErrorFeedback.ts`-ét).
  if (isCanceledRequest(err)) return fallback;

  if (!response) {
    // Válasz nélküli axios-hiba = a kérés elindult, de nem jött rá válasz.
    // SZÁNDÉKOSAN szűkítjük axios-hibára: a catch egy programozási hibát (pl. a
    // válaszfeldolgozásban dobott TypeError) is elkap, és arra a „nézd meg az
    // internetkapcsolatot” tanács hamis nyom lenne.
    const looksLikeRequestFailure =
      Boolean(err?.isAxiosError) || Boolean(err?.request) || typeof err?.code === 'string';

    return looksLikeRequestFailure ? NETWORK_ERROR_MESSAGE : fallback;
  }

  const serverMessage = serverErrorMessage(response.data);
  if (serverMessage) {
    return serverMessage;
  }

  // [Web UX 9 / T2, 2026-10-13] Testetlen (vagy nem saját törzsű) 403/429 → emberi magyar mondat
  // (lásd `statusSentence`); a szerver saját indoklása fent már elsőbbséget kapott.
  const general = statusSentence(response.status);
  if (general) return general;

  // Válaszolt, de nem indokolta. A státuszkód nélkül ez a mondat semmit nem mond a
  // hibakeresőnek — épp ezért írjuk ki.
  return response.status ? `${fallback} (HTTP ${response.status})` : fallback;
}

/**
 * Ugyanez `fetch`-hez: a válasz TESTÉBŐL szedi ki a szerver magyar üzenetét.
 *
 * MIÉRT KELL: a `fetch`-es hívások eddig `response.text()`-et dobtak tovább hibaüzenetként,
 * így a felhasználó NYERS JSON-t látott a felületen (pl. a jelenléti ív 403-ánál:
 * `{"message":"Csak az eseményhez rendelt edző (vagy adminisztrátor) rögzíthet jelenlétet."}`).
 *
 * ⚠️ CSAK a SAJÁT, MAGYAR indoklásunkat írjuk ki — SEMMI mást [2026-09-13-i szigorítás,
 * 2026-09-14-én kiterjesztve a burkolt és a validációs alakra: `serverErrorMessage`].
 *
 * Két mért hibát zár be:
 *  1. A ProblemDetails `title`-jét eddig elfogadtuk. A backend SOHA nem állít be `title`-t
 *     (mindig `new { message = … }`-t ad), tehát `title` kizárólag a keretrendszertől jöhet —
 *     és az MINDIG ANGOL: modell-kötési hibánál a válasz
 *     `{"title":"One or more validation errors occurred.", …}`. A felhasználó tehát angol
 *     ASP.NET-szöveget látott a végig magyar felületen.
 *  2. A nem-JSON törzset („rövid, olvasható szöveg") is átengedtük. A backend minden
 *     hibaválasza JSON `{ message }` (a nyers szöveges válaszokat 2026-09-13-án magyar
 *     JSON-ra cseréltük), ezért sima szöveg már csak infrastruktúrától (proxy, Kestrel,
 *     ASP.NET) jöhet — szintén angolul. Élesben mérve: `POST /api/attendance/bulk` nem létező
 *     eseményre `Event not found` sima szöveget adott, és ez KI IS ÍRÓDOTT a felületre.
 *
 * Ha a szerver egyik alakban sem indokolt, a hívó saját MAGYAR tartalék-mondata megy ki, a státuszkóddal — abból a
 * bejelentésből is látszik, mi történt, és a felhasználó nem kap idegen nyelvű szöveget.
 *
 * @param rawBody a `response.text()` eredménye (lehet JSON, sima szöveg vagy üres)
 * @param status  a HTTP-státusz — csak akkor jelenik meg, ha a szerver nem indokolt
 * @param fallback mit írjunk, ha a szerver nem mondta meg, mi a baj
 */
export function responseErrorMessage(rawBody: string | undefined | null, status: number, fallback: string): string {
  const body = (rawBody ?? '').trim();

  if (body) {
    try {
      // Ugyanaz a HÁROM alak, mint az axios-úton (`serverErrorMessage`) — a `fetch`-es
      // hívók eddig csak a csupasz `{ message }`-et kapták meg.
      const message = serverErrorMessage(JSON.parse(body));
      if (message) {
        return message;
      }
    } catch {
      // Nem JSON → nem a mi üzenetünk. Se a nyers szöveget, se a HTML hibaoldalt nem zúdítjuk
      // a felhasználóra; a magyar tartalék megy ki a státuszkóddal.
    }
  }

  // [Web UX 9 / T2, 2026-10-13] ugyanaz a 403/429-mondat, mint az axios-úton (`apiErrorMessage`)
  const general = statusSentence(status);
  if (general) return general;

  return status ? `${fallback} (HTTP ${status})` : fallback;
}
